import { useState, useMemo } from "react";
import { useGetTransactionsList } from "utils/api/user.api";
import { TRANSACTION_TYPE, type UserTransaction } from "utils/types/user.type";

export const useTransactionManagement = () => {
  const [activeTab, setActiveTab] = useState<string>(TRANSACTION_TYPE.WITHDRAW);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("completed");
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  // Map transaction type to API format
  const getApiType = (type: string): "Deposit" | "Withdraw" | "Swap" | "buy_sell" | undefined => {
    if (type === TRANSACTION_TYPE.WITHDRAW) return "Withdraw";
    if (type === TRANSACTION_TYPE.DEPOSIT) return "Deposit";
    if (type === TRANSACTION_TYPE.SWAP) return "Swap";
    if (type === "buy_sell") return "buy_sell";
    return undefined;
  };

  const { data: transactionsResponse, isLoading } = useGetTransactionsList({
    type: getApiType(activeTab),
    status: statusFilter.toLowerCase() as "completed" | "pending" | "rejected" | "in-progress",
    page,
    limit,
    search: searchValue || undefined,
  });

  const transactions = transactionsResponse?.data ?? [];

  // Transactions are already filtered by the API, but we can do client-side filtering if needed
  const filteredTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    // Additional client-side filtering if needed (API already handles most filtering)
    if (!searchValue) return transactions;
    
    const searchLower = searchValue.toLowerCase();
    return transactions.filter((transaction: UserTransaction) => {
      return (
        transaction.user?.email?.toLowerCase().includes(searchLower) ||
        String(transaction.type)?.toLowerCase().includes(searchLower) ||
        transaction.coin?.symbol?.toLowerCase().includes(searchLower) ||
        transaction.id?.toLowerCase().includes(searchLower) ||
        transaction.trxHash?.toLowerCase().includes(searchLower) ||
        new Date(transaction.createdAt)
          .toLocaleDateString()
          .includes(searchValue)
      );
    });
  }, [transactions, searchValue]);

  // Tab counts - we'll need to fetch stats for each type or count from current data
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    if (!transactions || transactions.length === 0) {
      counts[TRANSACTION_TYPE.WITHDRAW] = 0;
      counts[TRANSACTION_TYPE.DEPOSIT] = 0;
      const buySellTabId = "buy_sell";
      counts[buySellTabId] = 0;
      counts[TRANSACTION_TYPE.SWAP] = 0;
      return counts;
    }

    // Count transactions by type from current data
    counts[TRANSACTION_TYPE.WITHDRAW] = transactions.filter(
      (t: UserTransaction) => t.type === "Withdraw"
    ).length;

    counts[TRANSACTION_TYPE.DEPOSIT] = transactions.filter(
      (t: UserTransaction) => t.type === "Deposit"
    ).length;

    const buySellTabId = "buy_sell";
    counts[buySellTabId] = transactions.filter(
      (t: UserTransaction) => t.type === "Buy" || t.type === "Sell"
    ).length;

    counts[TRANSACTION_TYPE.SWAP] = transactions.filter(
      (t: UserTransaction) => t.type === "Swap"
    ).length;

    return counts;
  }, [transactions]);

  return {
    activeTab,
    setActiveTab,
    searchValue,
    setSearchValue,
    statusFilter,
    setStatusFilter,
    transactions: filteredTransactions,
    isLoading,
    tabCounts,
    page,
    setPage,
    pagination: transactionsResponse?.pagination,
  };
};

